/**
 * ElectionOverlay — 2024 precinct election results choropleth rendered inside TurfMap.
 *
 * Must be a child of react-map-gl <Map> so it can use useMap().
 *
 * Data flow:
 * 1. Listen for map moveend events (debounced 800ms)
 * 2. Determine which US states intersect the viewport
 * 3. Fetch per-state TopoJSON from NYT, decompress + convert to GeoJSON
 * 4. Merge state GeoJSON collections and render as fill + outline layers
 * 5. State data is cached permanently (never refetched)
 *
 * License: C-UDA (non-commercial) — demo/development use only.
 */

import { useCallback, useRef } from 'react';
import { Source, Layer } from 'react-map-gl/mapbox';
import { fetchStatePrecincts, getStatesInBounds, filterFeaturesByViewport } from '@/services/electionApi';
import { useViewportGeoJSON, type ViewportFetchArgs } from '@/hooks/useViewportGeoJSON';

export interface ElectionOverlayProps {
  visible: boolean;
  opacity?: number;
}

const MIN_ZOOM = 8; // Precinct data is detailed — only show when zoomed in enough
const DEBOUNCE_MS = 800;

/**
 * Build Mapbox GL fill-color expression for election results.
 * Red (Republican lead) → White (tie) → Blue (Democrat lead)
 */
function buildElectionFillColor() {
  return [
    'interpolate',
    ['linear'],
    ['coalesce', ['get', 'pct_dem_lead'], 0],
    -0.5, '#b71c1c',   // Strong Republican (deep red)
    -0.25, '#ef5350',   // Lean Republican
    -0.05, '#ffcdd2',   // Slight Republican
    0, '#f5f5f5',       // Tie (neutral)
    0.05, '#bbdefb',    // Slight Democrat
    0.25, '#42a5f5',    // Lean Democrat
    0.5, '#1565c0',     // Strong Democrat (deep blue)
  ];
}

export function ElectionOverlay({
  visible,
  opacity = 0.55,
}: ElectionOverlayProps) {
  const loadedStates = useRef(new Set<string>());
  const stateFeatures = useRef(new Map<string, GeoJSON.Feature[]>());

  const fetchForViewport = useCallback(
    async ({ bounds }: ViewportFetchArgs): Promise<GeoJSON.FeatureCollection | null> => {
      const visibleStates = getStatesInBounds(bounds);
      const needed = visibleStates.filter((s) => !loadedStates.current.has(s));

      if (needed.length > 0) {
        const batch = needed.slice(0, 3);
        await Promise.all(
          batch.map(async (stateCode) => {
            try {
              const fc = await fetchStatePrecincts(stateCode);
              loadedStates.current.add(stateCode);
              stateFeatures.current.set(stateCode, fc.features);
            } catch (err) {
              console.warn(`[ElectionOverlay] Failed to fetch ${stateCode}:`, err);
              loadedStates.current.add(stateCode);
              stateFeatures.current.set(stateCode, []);
            }
          }),
        );
      }

      // Build merged FeatureCollection from all loaded states visible in viewport.
      // Clip to viewport bbox — without this, multi-state collections (e.g. AR+TN+GA
      // ≈ 10k precincts) overflow JSON.stringify when Mapbox ships data to its worker.
      const allFeatures: GeoJSON.Feature[] = [];
      for (const state of visibleStates) {
        const features = stateFeatures.current.get(state);
        if (features) {
          allFeatures.push(...features);
        }
      }
      const visibleFeatures = filterFeaturesByViewport(allFeatures, bounds);

      return visibleFeatures.length > 0
        ? { type: 'FeatureCollection', features: visibleFeatures }
        : null;
    },
    [],
  );

  const { data: geojson } = useViewportGeoJSON(visible, fetchForViewport, {
    minZoom: MIN_ZOOM,
    debounceMs: DEBOUNCE_MS,
  });

  if (!geojson) return null;

  return (
    <Source id="election-precincts" type="geojson" data={geojson}>
      {/* Precinct fill — red/blue choropleth */}
      <Layer
        id="election-fill"
        type="fill"
        paint={{
          'fill-color': buildElectionFillColor() as never,
          'fill-opacity': opacity,
        }}
      />

      {/* Precinct outlines */}
      <Layer
        id="election-line"
        type="line"
        paint={{
          'line-color': '#ffffff',
          'line-width': 0.3,
          'line-opacity': 0.5,
        }}
      />
    </Source>
  );
}
